/**
 * @fileoverview Org portal — where a program's classes meet.
 *
 * The address is a PROGRAM fact: every class in the program meets there, and
 * it is what a published timetable's calendar blocks carry. It used to be
 * edited from a class's page, which implied it was a class field and left an
 * administrator setting it on one class while wondering why another still
 * could not publish. It lives here, beside the program's other settings, and
 * the class page's publish gate points at it.
 *
 * The timezone is the venue's, and it frames every "today" the program's
 * classes are counted or listed in. A new address starts on the
 * organization's time zone, never this browser's. Classes already set up in
 * another zone are listed under the card after a save, to move.
 */

import { useContext, useState } from "react";
import { toast } from "sonner";

import { OrgAuthContext } from "@/App";
import ScheduleZoneCheck from "@/components/org/ScheduleZoneCheck";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, SectionHeader } from "@/components/studio/primitives";
import TimezoneSelect from "@/components/TimezoneSelect";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

const FIELDS = [
  ["address_line1", "Street address", true],
  ["address_line2", "Address line 2", false],
  ["city", "City", true],
  ["region", "State or region", false],
  ["postal_code", "Postal code", false],
  ["country", "Country code", true],
];

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const blank = (zone) => ({
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "US",
  timezone: zone || browserZone(),
});

export default function ProgramLocationSettings({ program, onSaved }) {
  const { org } = useContext(OrgAuthContext) || {};
  const [address, setAddress] = useState({ ...blank(org?.timezone), ...(program.location_address || {}) });
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);
  const set = (key) => (e) => setAddress((a) => ({ ...a, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, { location_address: address });
      toast.success("Program location saved");
      setRevision((n) => n + 1);
      onSaved?.(res.data?.program);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save the program location"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionHeader>Program location</SectionHeader>
      <Card className="mb-6" data-testid="program-location">
        <form onSubmit={submit} className="space-y-4">
          <p className="font-sans text-sm text-[var(--text-secondary)]">
            Where this program&rsquo;s classes meet. Shared by every class in the program, and required before a
            timetable can be published to the calendar.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {FIELDS.map(([key, label, required]) => (
              <label key={key} className="block font-sans text-sm text-[var(--text-secondary)]">
                {label}
                <input
                  className={`${inputClass} mt-1`}
                  value={address[key] || ""}
                  required={required}
                  maxLength={key === "country" ? 2 : 200}
                  onChange={set(key)}
                  data-testid={`program-location-${key}`}
                />
              </label>
            ))}
            <label className="block font-sans text-sm text-[var(--text-secondary)]">
              Time zone
              <TimezoneSelect
                value={address.timezone || ""}
                onChange={set("timezone")}
                className={`${inputClass} mt-1`}
                required
                data-testid="program-location-timezone"
              />
            </label>
          </div>
          <Button type="submit" disabled={saving} data-testid="program-location-save">
            Save location
          </Button>
        </form>
      </Card>
      <div className="-mt-2 mb-6">
        <ScheduleZoneCheck programId={program.program_id} revision={revision} testId="program-zone-check" />
      </div>
    </>
  );
}
